import _ from 'lodash'

const alwaysInclude = Symbol('alwaysInclude')

/**
 * Tests whether, given a value representing a field, that field should be included in the response
 * that would go back to the API.
 *
 * @param {*} val - value used to check for field inclusion
 * @return {Boolean}
 * @private
 */
const checkValueForInclusion = val =>
  !(val === null || val === '' || val === undefined)

/**
 * Given an unordered list of objects, it splits the object by a key, which should be unique, to provide
 * the UI fields with a string key reference to object fields, which are easy to reference and update.
 *
 * @param {Array} - original collection of objects
 * @param {String} - key pointing to the value in each collection item that can be used as a unique reference
 * @return {Object<string, Object>}
 */
export const splitEnumArrayByField = ({ original, key }) => {
  const res = {}
  let fieldKey = ''

  // Some APIs will represent an empty list with no list at all. Handle that case.
  original = original || []

  original.forEach(field => {
    fieldKey = field[key]
    res[fieldKey] = field
  })

  return res
}

/**
 * Given an unordered list of objects, it groups items in the list by an enum key so that they may be used
 * independently. A common case for this is if you find yourself using `filter` on an array in api data.
 *
 * @param {Array} - original collection of objects
 * @param {String} - key pointing to the value in each collection item that can be used as a unique reference
 * @return {Object<string, Array>}
 */
export const groupEnumArrayByField = ({ original, key }) => {
  const res = {}
  let fieldKey = ''
  let existing

  // Some APIs will represent an empty list with no list at all. Handle that case.
  original = original || []

  original.forEach(field => {
    fieldKey = field[key]
    existing = res[fieldKey]

    if (Array.isArray(existing)) {
      existing.push(field)
    } else {
      res[fieldKey] = [field]
    }
  })

  return res
}

/**
 * Given an object, usually one split by the `splitEnumArrayByField` above, it merges back into the same format as the
 * original array, usually one an API has given us. It should preserve the original order, and be able to remove
 * elements that were persent in the original collection, which have been marked for deletion by the UI.
 *
 * @param {Array} original - original collection of objects
 * @param {Object} split - hash map produced by splitting using `splitEnumArrayByField`
 * @param {String} key - key pointing to the value in each collection item that can be used as a unique reference
 * @param {String} inclusionKey - If this field is nil or if empty, we omit it from the merge, which represents the
 *   deletion of the value from the array
 * @return {Array}
 */
export const mergeEnumArrayByField = ({
  original,
  split,
  key,
  inclusionKey = alwaysInclude,
}) => {
  const numValues = Object.keys(split).length
  const res = new Array(numValues)
  let runningIndex = 0
  let fieldKey

  // Some APIs will represent an empty list with no list at all. Handle that case.
  original = original || []

  // Because of how I structured split, having the new split values have constant time lookup, I can ensure I do this
  // mapping back in a single pass. No need to use findIndex here, because I can be sure that I will form the correct
  // array order by doing a lookup.
  //
  // NOTE: This assumption is only valid if they key from which we split is unique. Having said that, if the key isn't
  // unique, you shouldn't be using either of these methods.
  original.forEach((field, i) => {
    fieldKey = field[key]
    const splitField = split[fieldKey]

    if (splitField) {
      // Seen the computed field, but we now need to check whether this should be included
      // in the final result, and we do that by checking the value of the inclusion key. Usually components
      // will set this to be an empty value, either an empty string for regular inputs, or a null/undefined value.
      //
      // If this gets any more complex, we can introduce an inclusionPredicate, which accepts the whole value and
      // returns true if the value should be included. This solution is fine for what we do now.
      const inclusionCheckValue = splitField[inclusionKey]

      // Be sure to not do a pure falsey check here -- a value of 0 and false is perfectly valid here
      const shouldBeIncluded = checkValueForInclusion(inclusionCheckValue)

      if (inclusionKey === alwaysInclude || shouldBeIncluded) {
        res[runningIndex] = {
          ...splitField,
          [key]: fieldKey,
        }
      }

      delete split[fieldKey]
    } else {
      res[runningIndex] = field
    }

    runningIndex++
  })

  // Exhausted the original fields, so if we have any remaining, they are new computed fields
  for (const [newKey, newField] of Object.entries(split)) {
    res[runningIndex] = {
      ...newField,
      [key]: newKey,
    }

    runningIndex++
  }

  // Some value may have been omitted since their value was removed, so we have a potentially sparse array, which is
  // why we compact here
  return _.compact(res)
}
